import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft,
  Save,
  Play,
  Plus,
  FileText,
  TrendingDown,
  Trash2,
  Download,
  MoreVertical,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { exportToCSV, exportToExcel, generateFilename, formatCurrencyForExport, type ExportColumn } from '@/lib/export-utils';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { AssetSelectionDialog } from '@/components/depreciation/asset-selection-dialog';
import { BatchDetailGrid } from '@/components/depreciation/batch-detail-grid';
import type { DepreciationBatch, DepreciationItem } from '@/types/depreciation';
import type { Asset } from '@/types/asset';
import { dataService } from '@/services/data-service';
import { formatApiErrorMessage } from '@/lib/format-utils';
import { formatCurrency } from '@/lib/format-currency';
import { DepreciationCalculator } from '@/lib/depreciation-calculator';

const getDepreciationExportColumns = (): ExportColumn<DepreciationItem>[] => [
  { label: 'Asset Code', key: 'assetCode' as keyof DepreciationItem },
  { label: 'Asset Description', key: 'assetDescription' as keyof DepreciationItem },
  { label: 'Category', key: 'category' as keyof DepreciationItem },
  { label: 'Location', key: 'location' as keyof DepreciationItem },
  { label: 'Opening NBV', key: 'openingNBV' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Acquisition Cost', key: 'acquisitionCost' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Accumulated Depreciation', key: 'accumulatedDepreciation' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Method', key: 'method' as keyof DepreciationItem },
  { label: 'Useful Life (Years)', key: 'usefulLife' as keyof DepreciationItem },
  { label: 'Life Remaining (Years)', key: 'lifeRemaining' as keyof DepreciationItem, format: (val: number) => val?.toFixed(2) ?? '' },
  { label: 'Residual Value', key: 'residualValue' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Depreciation %', key: 'depreciationPercent' as keyof DepreciationItem, format: (val: number) => val ? `${val.toFixed(2)}%` : '' },
  { label: 'Depreciation Value', key: 'depreciationValue' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Closing NBV', key: 'closingNBV' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Manual Override', key: 'isManualOverride' as keyof DepreciationItem, format: (val: boolean) => val ? 'Yes' : 'No' },
  { label: 'Override Reason', key: 'overrideReason' as keyof DepreciationItem },
  { label: 'Notes', key: 'notes' as keyof DepreciationItem },
];

interface DepreciationEntryScreenProps {
  batch: DepreciationBatch;
  onClose: () => void;
  onSave: (batch: DepreciationBatch) => void;
}

export function DepreciationEntryScreen({
  batch: initialBatch,
  onClose,
  onSave,
}: DepreciationEntryScreenProps): React.ReactElement {
  const navigate = useNavigate();
  
  // State for batch header
  const [batchReference, setBatchReference] = useState(initialBatch?.batchReference ?? '');
  const [dateOfRun, setDateOfRun] = useState(
    initialBatch?.dateOfRun || new Date().toISOString().split('T')[0]
  );
  const [notes, setNotes] = useState(initialBatch?.notes ?? '');
  
  // State for depreciation items
  const [items, setItems] = useState<DepreciationItem[]>(initialBatch?.items || []);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  
  // State for modals
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showProcessConfirm, setShowProcessConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showAssetSelection, setShowAssetSelection] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Computed values
  const isReadOnly = initialBatch?.status === 'Completed';
  const totalDepreciation = items.reduce((sum, item) => sum + item.depreciationValue, 0);
  const totalOpeningNBV = items.reduce((sum, item) => sum + item.openingNBV, 0);
  const totalClosingNBV = items.reduce((sum, item) => sum + item.closingNBV, 0);
  const allSelected = items.length > 0 && selectedItemIds.size === items.length;

  // Mark as having unsaved changes when any field changes (skip initial render)
  const [isInitialRender, setIsInitialRender] = useState(true);
  
  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
      return;
    }
    setHasUnsavedChanges(true);
  }, [batchReference, dateOfRun, notes, items]);

  const handleSave = async () => {
    // Validate required fields
    if (!batchReference.trim()) {
      toast.error('Reference is required', {
        description: 'Please enter a batch reference before saving.',
      });
      return;
    }

    if (!dateOfRun) {
      toast.error('Date is required', {
        description: 'Please select a date before saving.',
      });
      return;
    }

    if (items.length === 0) {
      toast.error('No assets selected', {
        description: 'Please add at least one asset to the depreciation batch.',
      });
      return;
    }

    const now = new Date().toISOString();
    
    const batch: DepreciationBatch = {
      id: initialBatch?.id || `batch-${Date.now()}`,
      batchReference,
      dateOfRun,
      projectId: initialBatch?.projectId || '',
      projectName: initialBatch?.projectName || '',
      notes,
      items,
      status: 'In Progress',
      preparedBy: initialBatch?.preparedBy || 'Current User',
      preparedDate: initialBatch?.preparedDate || now,
      totalDepreciation,
      totalOpeningNBV,
      totalClosingNBV,
      currency: 'GBP',
      createdBy: initialBatch?.createdBy || 'Current User',
      createdDate: initialBatch?.createdDate || now,
      modifiedBy: 'Current User',
      modifiedDate: now,
      currentStep: 'Prepare',
    };

    try {
      onSave(batch);
      setHasUnsavedChanges(false);
      toast.success('Batch saved successfully');
    } catch (error) {
      console.error('Failed to save depreciation batch:', error);
      toast.error('Failed to save batch', {
        description: 'Please try again.',
      });
    }
  };

  const handleProcess = () => {
    // Validate required fields
    if (!batchReference.trim()) {
      toast.error('Reference is required', {
        description: 'Please enter a batch reference before processing.',
      });
      return;
    }

    if (!dateOfRun) {
      toast.error('Date is required', {
        description: 'Please select a date before processing.',
      });
      return;
    }

    if (items.length === 0) {
      toast.error('No assets selected', {
        description: 'Please add at least one asset to the depreciation batch.',
      });
      return;
    }

    // Validate that at least one item has a non-zero depreciation value
    const hasValidItems = items.some(item => item.depreciationValue > 0);
    if (!hasValidItems) {
      toast.error('No depreciation values', {
        description: 'Please ensure at least one asset has a non-zero depreciation value.',
      });
      return;
    }

    setShowProcessConfirm(true);
  };

  const confirmProcess = async () => {
    try {
      const reference = batchReference;
      
      // Ensure batch is saved first
      const isServerGeneratedId = (id: string) => {
        const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return guidRegex.test(id);
      };

      let batchId = initialBatch?.id;
      
      if (!isServerGeneratedId(initialBatch?.id || '')) {
        const now = new Date().toISOString();
        const batchToSave: DepreciationBatch = {
          id: initialBatch?.id || `batch-${Date.now()}`,
          batchReference,
          dateOfRun,
          projectId: initialBatch?.projectId || '',
          projectName: initialBatch?.projectName || '',
          notes,
          items,
          status: 'In Progress',
          preparedBy: initialBatch?.preparedBy || 'Current User',
          preparedDate: initialBatch?.preparedDate || now,
          totalDepreciation,
          totalOpeningNBV,
          totalClosingNBV,
          currency: 'GBP',
          createdBy: initialBatch?.createdBy || 'Current User',
          createdDate: initialBatch?.createdDate || now,
          modifiedBy: 'Current User',
          modifiedDate: now,
          currentStep: 'Prepare',
        };
        const savedBatch = await dataService.createDepreciationBatch(batchToSave);
        batchId = savedBatch.id;
      } else {
        // Update the batch to ensure latest changes are saved
        const now = new Date().toISOString();
        const batchToUpdate: DepreciationBatch = {
          ...initialBatch,
          batchReference,
          dateOfRun,
          notes,
          items,
          totalDepreciation,
          totalOpeningNBV,
          totalClosingNBV,
          modifiedBy: 'Current User',
          modifiedDate: now,
        };
        await dataService.updateDepreciationBatch(initialBatch.id, batchToUpdate);
      }
      
      // Call the backend PostBatch endpoint
      const result = await dataService.postDepreciationBatch(batchId!, 'Current User');
      
      // Check for posting failures
      const postingResult = result?.postingResult;
      const hasCreationFailures = postingResult?.transactionCreationFailures?.length > 0;
      const hasPostingFailures = postingResult?.financialsPostingFailures?.length > 0;
      
      if (hasCreationFailures || hasPostingFailures) {
        const failureMessages: string[] = [];
        const errorDetails: string[] = [];
        
        if (hasCreationFailures) {
          const count = postingResult.transactionCreationFailures.length;
          failureMessages.push(`${count} transaction(s) failed to create`);
          postingResult.transactionCreationFailures.forEach((failure: { assetCode: string; errorMessage: string }) => {
            errorDetails.push(`Asset '${failure.assetCode}': ${formatApiErrorMessage(failure.errorMessage)}`);
          });
        }
        
        if (hasPostingFailures) {
          const count = postingResult.financialsPostingFailures.length;
          failureMessages.push(`${count} transaction(s) failed to post to Financials`);
          postingResult.financialsPostingFailures.forEach((failure: { assetCode: string; errorMessage: string }) => {
            errorDetails.push(`Asset '${failure.assetCode}': ${formatApiErrorMessage(failure.errorMessage)}`);
          });
        }
        
        const maxErrorsToShow = 3;
        const displayedErrors = errorDetails.slice(0, maxErrorsToShow);
        const remainingCount = errorDetails.length - maxErrorsToShow;
        let description = `Reference: ${reference}. ${failureMessages.join('. ')}.\n${displayedErrors.join('\n')}`;
        if (remainingCount > 0) {
          description += `\n...and ${remainingCount} more error(s)`;
        }
        
        toast.warning('Depreciation Completed with Warnings', {
          description,
          duration: 10000,
        });
      } else {
        toast.success('Depreciation Created Successfully', {
          description: `Reference: ${reference}`,
        });
      }
      
      setShowProcessConfirm(false);
      onClose(); // Close the entry screen and return to the landing page
      navigate('/transactions/depreciation');
    } catch (error) {
      console.error('Failed to process depreciation batch:', error);
      toast.error('Failed to process depreciation batch', {
        description: 'Please ensure you are in API mode and try again.',
      });
      setShowProcessConfirm(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  };

  const confirmCancel = () => {
    setShowCancelConfirm(false);
    onClose();
  };

  const handleRemoveSelectedItems = () => {
    if (selectedItemIds.size === 0) return;
    setShowRemoveConfirm(true);
  };

  const confirmRemoveSelectedItems = () => {
    setItems(prevItems => prevItems.filter(item => !selectedItemIds.has(item.id)));
    setSelectedItemIds(new Set());
    setShowRemoveConfirm(false);
  };

  const handleAddMoreAssets = () => {
    setShowAssetSelection(true);
  };

  const handleAssetsSelected = (selectedAssets: Asset[]) => {
    // Filter out duplicates based on assetId
    const existingAssetIds = new Set(items.map(item => item.assetId));
    const uniqueNewAssets = selectedAssets.filter(asset => !existingAssetIds.has(asset.id));

    // Create depreciation items from selected assets
    const newItems: DepreciationItem[] = uniqueNewAssets.map((asset, index) => {
      // Use the DepreciationCalculator to calculate depreciation based on asset's method
      const calculation = DepreciationCalculator.calculateDepreciation(asset, new Date(), 12);
      
      const usefulLifeYears = asset.usefulLife ?? 5;
      const depreciationMethod = asset.depreciationMethod ?? 'Straight Line';

      return {
        id: `dep-item-${Date.now()}-${index}`,
        assetId: asset.id,
        assetCode: asset.code,
        assetDescription: asset.name,
        category: asset.category,
        location: asset.location,
        openingNBV: asset.netBookValue,
        acquisitionCost: asset.acquisitionCost,
        accumulatedDepreciation: asset.accumulatedDepreciation,
        method: depreciationMethod as any,
        usefulLife: usefulLifeYears,
        lifeRemaining: usefulLifeYears - (asset.accumulatedDepreciation / asset.acquisitionCost) * usefulLifeYears,
        residualValue: asset.residualValue ?? 0,
        aiDepreciationPercent: calculation.depreciationPercent ?? 0,
        aiDepreciationValue: calculation.depreciationValue,
        aiClosingNBV: calculation.closingNBV,
        confidence: 'High' as const,
        depreciationPercent: calculation.depreciationPercent ?? 0,
        depreciationValue: calculation.depreciationValue,
        closingNBV: calculation.closingNBV,
        isManualOverride: false,
        validationStatus: 'valid',
        calculationInputs: calculation.calculationInputs ?? {
          method: depreciationMethod,
          residualValue: asset.residualValue ?? 0,
          formula: calculation.formula,
          formulaBreakdown: calculation.formulaBreakdown ?? '',
        },
      };
    });

    setItems([...items, ...newItems]);
    setShowAssetSelection(false);
    setHasUnsavedChanges(true);
    
    if (uniqueNewAssets.length > 0) {
      toast.success(`Added ${uniqueNewAssets.length} asset${uniqueNewAssets.length === 1 ? '' : 's'} to depreciation batch`);
    } else {
      toast.info('No new assets added', {
        description: 'All selected assets are already in the batch.',
      });
    }
  };

  const handleUpdateItem = (itemId: string, updates: Partial<DepreciationItem>) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? { ...item, ...updates } : item
      )
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItemIds(new Set(items.map(item => item.id)));
    } else {
      setSelectedItemIds(new Set());
    }
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    setSelectedItemIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(itemId);
      } else {
        newSet.delete(itemId);
      }
      return newSet;
    });
  };

  const handleExportCSV = () => {
    if (items.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      exportToCSV({
        data: items,
        columns: getDepreciationExportColumns(),
        filename: generateFilename(`depreciation-batch-${batchReference || 'draft'}`, 'csv'),
      });

      toast.success('Export successful', {
        description: 'Depreciation batch exported to CSV',
      });
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      toast.error('Export failed', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  const handleExportExcel = () => {
    if (items.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      exportToExcel({
        data: items,
        columns: getDepreciationExportColumns(),
        filename: generateFilename(`depreciation-batch-${batchReference || 'draft'}`, 'xlsx'),
        sheetName: 'Depreciation Details',
      });

      toast.success('Export successful', {
        description: 'Depreciation batch exported to Excel',
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Export failed', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  return (
    <div className="flex h-full flex-col" style={{ backgroundColor: 'oklch(0.984 0.003 247.858)' }}>
      {/* Compact Header */}
      <div className="border-b bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-sm flex-shrink-0">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={handleCancel} className="h-9 w-9">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-3">
                <TrendingDown className="h-5 w-5 text-primary" />
                <div>
                  <h1 className="text-xl font-bold">
                    {batchReference ? `Depreciation - ${batchReference}` : 'New Depreciation'}
                  </h1>
                  {isReadOnly && initialBatch?.postedDate ? (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Posted: {new Date(initialBatch.postedDate).toLocaleString()}
                    </p>
                  ) : initialBatch?.modifiedDate ? (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Last modified: {new Date(initialBatch.modifiedDate).toLocaleString()}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
                        <div className="flex items-center gap-2">
                          {!isReadOnly && (
                            <>
                              <Button variant="outline" onClick={handleSave} className="h-9">
                                <Save className="mr-2 h-4 w-4" />
                                Save
                              </Button>
                              <Button onClick={handleProcess} className="h-9">
                                <Play className="mr-2 h-4 w-4" />
                                Process
                              </Button>
                            </>
                          )}
                          {isReadOnly && (
                            <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              Completed
                            </Badge>
                          )}
                          {items.length > 0 && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="icon" className="h-9 w-9">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={handleExportCSV}>
                                  <Download className="mr-2 h-4 w-4" />
                                  Export to CSV
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleExportExcel}>
                                  <Download className="mr-2 h-4 w-4" />
                                  Export to Excel
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto pb-16">
        <div className="space-y-4 p-4">
          {/* Header Details Card */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden rounded-lg">
            <div className="p-6">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Header Details
              </h3>
              <div className="grid grid-cols-12 gap-4">
                {/* Row 1: Reference and Notes */}
                <div className="col-span-3">
                  <Label htmlFor="reference" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Reference *
                  </Label>
                  <Input
                    id="reference"
                    value={batchReference}
                    onChange={(e) => setBatchReference(e.target.value.slice(0, 10))}
                    placeholder="Enter reference..."
                    maxLength={10}
                    readOnly={isReadOnly}
                    className="h-9 placeholder:italic"
                    style={{ fontStyle: 'normal' }}
                    tabIndex={1}
                  />
                </div>

                {/* Notes - Right side spanning 2 rows */}
                <div className="col-span-6 row-span-2 col-start-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <Label htmlFor="notes" className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      Notes
                    </Label>
                    <span className="text-xs text-muted-foreground">{notes.length}/400</span>
                  </div>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value.slice(0, 400))}
                    placeholder="Enter any additional notes..."
                    rows={2}
                    maxLength={400}
                    readOnly={isReadOnly}
                    className="resize-none text-sm h-[110px] placeholder:italic"
                    style={{ fontStyle: 'normal' }}
                    tabIndex={3}
                  />
                </div>

                {/* Row 2: Date (below Reference) */}
                <div className="col-span-3 col-start-1">
                  <Label htmlFor="date" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Date *
                  </Label>
                  <Input
                    id="date"
                    type="date"
                    value={dateOfRun}
                    onChange={(e) => setDateOfRun(e.target.value)}
                    readOnly={isReadOnly}
                    className="h-9 placeholder:italic"
                    style={{ fontStyle: 'normal' }}
                    tabIndex={2}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Transaction Details */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden rounded-lg">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Transaction Details
                <Badge variant="secondary" className="ml-2">{items.length} {items.length === 1 ? 'Asset' : 'Assets'}</Badge>
              </h3>
              {!isReadOnly && (
                <div className="flex items-center gap-2">
                  {selectedItemIds.size > 0 && (
                    <Button type="button" variant="destructive" size="sm" onClick={handleRemoveSelectedItems} className="h-8">
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      Remove Lines ({selectedItemIds.size})
                    </Button>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={handleAddMoreAssets} className="h-8">
                    <Plus className="mr-2 h-3.5 w-3.5" />
                    Add Assets
                  </Button>
                </div>
              )}
            </div>

            {items.length > 0 ? (
              <div className="overflow-hidden">
                <BatchDetailGrid 
                  items={items} 
                  onUpdateItem={handleUpdateItem} 
                  readonly={isReadOnly}
                  selectedItemIds={selectedItemIds}
                  onSelectionChange={setSelectedItemIds}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="rounded-full bg-muted p-3 mb-4">
                  <FileText className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-lg mb-1">No Assets Added</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Add assets to this depreciation batch to get started
                </p>
                {!isReadOnly && (
                  <Button variant="outline" size="sm" onClick={handleAddMoreAssets}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Assets
                  </Button>
                )}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Fixed Bottom Footer */}
      {items.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-t border-slate-200 dark:border-slate-800 shadow-lg z-10">
          <div className="px-6 py-3">
            <div className="flex justify-end items-center gap-4 max-w-7xl ml-auto">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Total Opening NBV</span>
                <span className="text-base font-bold text-slate-900 dark:text-slate-100">{formatCurrency(totalOpeningNBV)}</span>
              </div>
              <div className="w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Total Depreciation</span>
                <span className="text-base font-bold text-slate-900 dark:text-slate-100">{formatCurrency(totalDepreciation)}</span>
              </div>
              <div className="w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Total Closing NBV</span>
                <span className="text-base font-bold text-slate-900 dark:text-slate-100">{formatCurrency(totalClosingNBV)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Asset Selection Dialog */}
      <AssetSelectionDialog
        open={showAssetSelection}
        onOpenChange={setShowAssetSelection}
        onConfirm={handleAssetsSelected}
        excludeAssetIds={items.map(item => item.assetId)}
      />

      {/* Confirmation Modals */}
      <ConfirmationModal
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        onConfirm={confirmCancel}
        title="Cancel Depreciation Batch?"
        description="Are you sure you want to cancel this batch? All unsaved changes will be lost and cannot be recovered."
        confirmText="Yes, Cancel Batch"
        cancelText="Keep Working"
        type="warning"
      />

      <ConfirmationModal
        open={showProcessConfirm}
        onOpenChange={setShowProcessConfirm}
        onConfirm={confirmProcess}
        title="Process Depreciation Batch"
        description={
          <div className="space-y-2">
            <p>This will post the depreciation transactions to the ledger.</p>
            <div className="mt-4 p-3 bg-muted rounded-md space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Assets:</span>
                <span className="font-medium">{items.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Depreciation:</span>
                <span className="font-medium">£{totalDepreciation.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-3">This action cannot be undone.</p>
          </div>
        }
        confirmText="Process Batch"
        cancelText="Review Again"
        type="success"
      />

      <ConfirmationModal
        open={showRemoveConfirm}
        onOpenChange={setShowRemoveConfirm}
        onConfirm={confirmRemoveSelectedItems}
        title="Remove Selected Assets"
        description={`Are you sure you want to remove ${selectedItemIds.size} selected asset(s) from this batch? This action cannot be undone.`}
        confirmText="Remove Assets"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
}
